import Claim from '../model/Claim';
import ClaimCategory from '../model/ClaimCategory';
import ClaimType from '../model/ClaimType';
import Document from '../model/Document';
import Event from '../model/Event';
import m from './module';
import {
  getProvisionalIdentifierForType,
  isProvisionalIdentifier,
  normalizeIdentifier,
  normalizeSingleDayStartEndDates,
  isDateObject,
  formatCurrency,
  uuidv4,
  castToBoolean,
} from '../util';
import { createError } from '../error';
import { INCREMENTAL_PURGE_OLD_DATA_STORE } from "../constants";
import { cloneDeep } from "lodash";

// const path = '/claim_category';
const getClaimsForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/claims';
const getArchiveClaimsForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/claims';
const savePrimaryClaimForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/positions/{{positions_id}}/primary_claim';
const savePrimaryClaimForOtherStaffingUserPath = '/staffing_user/{{staffing_users_id}}/positions/{{positions_id}}/other_users_primary_claim';
const saveSecondaryClaimForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/secondary_claims';
const uploadSecondaryClaimReceipt = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/secondary_claims/{{claims_id}}/upload';
const withdrawSecondaryClaim = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/secondary_claims/{{claims_id}}/withdraw';
const getOtherUserClaimsForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/other_users_claims';

const dateFields = [
  'pay_date',
  'claim_date',
  'transaction_date',
];

const dateTimeFields = [
  'created_at',
  'updated_at',
  'approved_at',
  'declined_at',
  'withdrawn_at',
  'attested_at',
];

const positionDateTimeFields = [
  'actual_start',
  'actual_end',
  'meal_break_start',
  'meal_break_end',
  'rest_break_start',
  'rest_break_end',
];

const timeFields = [
  'claim_start_time',
  'claim_end_time',
];

class ClaimService {
  constructor(
    $q,
    $log,
    $injector,
    APIService,
    DataSyncService,
    IncrementalDataStoreService,
    LogService,
    LanguageService,
    ClaimStatusService,
    ClaimCategoryService,
    PositionClaimTypeService,
    EventService,
    PayrollPeriodService,
    DocumentService,
    PositionTypeService,
    DateTimeService
  ) {
    this.$q = $q;
    this.$log = $log;
    this.$injector = $injector;
    this.APIService = APIService;
    this.DataSyncService = DataSyncService;
    this.IncrementalDataStoreService = IncrementalDataStoreService;
    this.LogService = LogService;
    this.LanguageService = LanguageService;
    this.ClaimStatusService = ClaimStatusService;
    this.ClaimCategoryService = ClaimCategoryService;
    this.PositionClaimTypeService = PositionClaimTypeService;
    this.EventService = EventService;
    this.PayrollPeriodService = PayrollPeriodService;
    this.DocumentService = DocumentService;
    this.PositionTypeService = PositionTypeService;
    this.DateTimeService = DateTimeService;
    this._getAllForStaffUserPromiseMap = {};
    this._getByIdPromiseMap = {};
    this._objectsByIdMap = {};
    this._getForEventPromiseMap = {};
    this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName = {};
    this._mostRecentDataStore = null;

    const expireIncrementalDataHandler = () => {
      return this.$q.when(this._mostRecentDataStore)
        .then(dataStore => {
          if (dataStore && dataStore.setNeedsUpdate) {
            dataStore.setNeedsUpdate();
          }
        });
    };

    // note: added "pre-refresh" hooks while debugging MOSAICPT-2127
    // not sure why we had it set up like this previously.. every time we do a sync, we would mark the data as stale
    // and cause it to require being re-downloaded
    this.DataSyncService.addPreRefreshPositionsCallback(expireIncrementalDataHandler);
    this.DataSyncService.addPreRefreshDataCallback(expireIncrementalDataHandler);
  }

  create(data, config = null) {
    const promises = [];
    const afterEventLoadedPromises = [];
    data = data || {};
    if (!data.id) {
      data.id = getProvisionalIdentifierForType("claim");
    }

    // Populate UUID
    if (!data.app_uuid) {
      data.app_uuid = uuidv4();
    }

    // Populate event
    let eventPromise;

    // Populate event if present
    if (data.event) {
      if (data.event instanceof Event) {
        eventPromise = this.$q.when(data.event);
      } else {
        // Create event if it is not already an Event object
        // This happens in the case of partial claims, possibly elsewhere.
        eventPromise = this.EventService.getOrCreateWithData(data.event, config);
      }
    } else if (data.events_id) {
      // Retrieve event if it is not embedded in object
      eventPromise = this.EventService.getById(data.events_id, config)
        .catch(err => {
          this.LogService.warn(`Error getting event for position ${data.id}:`, err);
          return this.$q.reject(err);
        })
    } else if (data.position && data.position.event) {
      // Load event from position
      if (data.position.event instanceof Event) {
        eventPromise = this.$q.when(data.position.event);
      } else {
        eventPromise = this.EventService.create(data.event, config);
      }
    } else {
      eventPromise = this.$q.when(null);
    }

    // Populate position type
    if (data.position_type) {
      promises.push(
        this.PositionTypeService.create(data.position_type)
          .then(positionType => {
            data.positionType = positionType;
          })
      )
    }

    promises.push(
      eventPromise
        .then(event => {
          const eventTimezoneName = event && event.timezoneName;
          data.event = event;

          if (!event) {
            this.LogService.warn(`ClaimService.create() no event for claim with data`, data);
          }

          // Convert dates (not date/times) using local timezone
          for (let field of dateFields) {
            if (data[field]) {
              data[field] = this.DateTimeService.parseDate(data[field]);
            }
          }

          // Convert date/times using server timezone
          for (let field of dateTimeFields) {
            data[field] = this.DateTimeService.parseDateWithServerTimezone(data[field]);
          }

          // Convert claim times, using event timezone
          let dateForTimeFields = data.claim_date || null;
          for (let field of timeFields) {
            if (data[field]) {
              data[field] = this.DateTimeService.parseTimeWithTimezoneOrServerTimezone(data[field], dateForTimeFields, eventTimezoneName);
            }
          }
          return event;
        })
    );

    // Populate status
    if (data.claim_statuses_id) {
      afterEventLoadedPromises.push(
        this.ClaimStatusService.getById(data.claim_statuses_id, config)
          .then(claimStatus => data.status = claimStatus)
      )
    }

    // Populate category
    const claimCategoryId = data.claim_categories_id
      || (data.category && data.category.id);
    const claimCategory = data.category;
    if (claimCategoryId && (!claimCategory || !(claimCategory instanceof ClaimCategory))) {
      afterEventLoadedPromises.push(
        this.ClaimCategoryService.getById(claimCategoryId, config)
          .then(claimCategory => data.category = claimCategory)
      )
    }

    // Populate type
    const claimTypeId = data.claim_types_id
      || (data.type && data.type.id);
    const claimType = data.type;
    if (claimTypeId && (!claimType || !(claimType instanceof ClaimType))) {
      afterEventLoadedPromises.push(
        this.PositionClaimTypeService.getById(claimTypeId, config)
          .then(claimType => {
            data.type = claimType;
          })
          .catch(err => {
            this.LogService.warn(`ClaimService.create() error getting claim type:`, err);
            return this.$q.reject(err);
          })
      )
    }

    // Create "thin" document (don't load all fields from server)
    const receiptDocumentsId = data.receipt_documents_id
      || (data.receiptDocument && data.receiptDocument.id);
    const receiptDocument = data.receiptDocument;
    if (receiptDocumentsId && (!receiptDocument || !(receiptDocument instanceof Document))) {
      afterEventLoadedPromises.push(
        this.DocumentService.getThinById(receiptDocumentsId)
          .then(document => {
            data.receiptDocument = document;
            return document;
          })
      )
    }

    // Populate payroll period if present
    if (data.payroll_period) {
      afterEventLoadedPromises.push(
        this.PayrollPeriodService.create(data.payroll_period, config)
          .then(payrollPeriod => {
            data.payrollPeriod = payrollPeriod;
            delete data.payroll_period;
          })
      )
    }

    return this.$q.all(promises)
      .then(() => this.$q.all(afterEventLoadedPromises))
      .then(() => {
        const model = new Claim(data);
        this._objectsByIdMap[data.id] = model;
        if (model.claim_types_id && !model.type) {
          // Second attempt to populate type
          return this.populateTypeForClaim(model)
            .then(() => model);
        }
        return model;
      })
      .then(claim => {
        return claim;
      })
  }

  getOrCreateWithData(data, config = null) {
    const id = data && data.id || null;
    return this.getLocalById(id)
      .then(claim => {
        if (claim) {
          return claim;
        }
        return this.create(data, config);
      });
  }

  markClaimWithdrawnForClaimId(claimId) {
    // TODO: Retrieve this claim and update status to withdrawn;
    return this.ClaimStatusService.getWithdrawnStatus()
  }

  populateTypeForClaim(claim) {
    return this.PositionClaimTypeService.getById(claim.claim_types_id)
      .then(claimType => {
        claim.type = claimType;
        return claimType;
      })
  }

  createForUser(staffingUser, data, config = null) {
    const promises = [];
    data = data || {};

    data.staffing_users_id = staffingUser.id;

    // Populate position if not present
    if (data.events_staffing_days_positions_id && !data.position) {
      let PositionService = this.$injector.get('PositionService');
      promises.push(
        PositionService.getByIdForStaffingUser(staffingUser, data.events_staffing_days_positions_id, config)
          .then(position => {
            data.position = position
          })
      )
    }

    return this.$q.all(promises)
      .then(() => this.create(data));
  }

  serialize(claim) {
    let data = {...claim};

    if (claim.category) data.claim_categories_id = claim.category.id;
    delete data.category;

    if (claim.type) data.claim_types_id = claim.type.id;
    delete data.type;

    if (claim.status) data.claim_statuses_id = claim.status.id;
    delete data.status;

    if (claim.routeSegment) {
      data.route_segments_id = claim.routeSegment.id;
      delete data.routeSegment;
    }


    if (claim.position) {
      data.events_staffing_days_positions_id = claim.position.id;
      if (claim.position.claim_date) {
        data.claim_date = claim.position.claim_date;
      }
      if (claim.position.shiftSegment) {
        data.shift_segments_id = claim.position.shiftSegment.id;
      }
      delete data.position;
    }


    if (claim.event) data.events_id = claim.event.id;
    delete data.event;

    // Convert dates that were parsed in server timezone
    for (let field of dateFields) {
      if (data[field]) {
        data[field] = this.DateTimeService.serializeDateTimeForAPI(data[field]);
      }
    }

    // Convert datetimes
    for (let field of dateTimeFields) {
      if (data[field]) {
        data[field]  = this.DateTimeService.serializeDateTimeForAPI(data[field]);
      }
    }

    // Convert times
    for (let field of timeFields) {
      if (data[field]) {
        data[field] = this.DateTimeService.serializeTimeForAPI(data[field]);
      }
    }

    // Used for backend calculations
    data.hours = claim.claimDurationInHours;

    data.primary_claim = castToBoolean(claim.primary_claim) ? 't' : 'f';

    return data;
  }

  serializePrimaryClaim(claim, position, eventTimezoneName = null) {
    if (position.claim_date)
    {
      claim.claim_date = position.claim_date;
    }
    if (position.shiftSegment)
    {
      position.shift_segments_id = position.shiftSegment.id;
    }

    if (!eventTimezoneName) {
      eventTimezoneName = (claim && claim.event && claim.event.timezoneName)
        || (position && position.event && position.event.timezoneName);
    }

    return {
      primary_claim: this.serialize(claim),
      position: this.serializePositionForPrimaryClaim(position, eventTimezoneName),
    };
  }

  /**
   * Serialize position that will be included with primary claim.
   * Date times should be first converted to the event timezone before serializing.
   */
  serializePositionForPrimaryClaim(position, eventTimezoneName = null) {
    if (!eventTimezoneName) {
      eventTimezoneName = position && position.event && position.event.timezoneName;
    }
    let data = {
      id: position.id,
      real_events_id: position.real_events_id,
      actual_hours: position.actual_hours,
      shift_segments_id : (position.shiftSegment) ? position.shiftSegment.id : null
    };

    // Normalize start/end dates
    if (isDateObject(position.actual_start) && isDateObject(position.actual_end)) {
      [position.actual_start, position.actual_end] = normalizeSingleDayStartEndDates(position.actual_start, position.actual_end);
    }

    // Add break acknowledgement fields to data.
    data['meal_break_acknowledgement'] = (
      position.hasOwnProperty('meal_break_acknowledgement')
      && position.meal_break_acknowledgement !== null
      && position.meal_break_acknowledgement.hasOwnProperty('id')
    )
      ? position.meal_break_acknowledgement.id
      : null
    data['rest_break_acknowledgement'] = (
      position.hasOwnProperty('rest_break_acknowledgement')
      && position.rest_break_acknowledgement !== null
      && position.rest_break_acknowledgement.hasOwnProperty('id')
    )
      ? position.rest_break_acknowledgement.id
      : null

    for (let field of positionDateTimeFields) {
      if (position[field]) {
        if (typeof position[field] === 'object' && Object.keys(position[field]).length >= 1) {
          data[field] = {}
          for (const property in position[field]) {
            data[field][property] = this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(position[field][property], eventTimezoneName);
          }
        } else {
          data[field] = this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(position[field], eventTimezoneName);
        }
      } else {
        data[field] = null;
      }
    }

    return data;
  }

  updatePositionFromAPI(position, updatedPositionData, eventTimezoneName = null) {
    if (!eventTimezoneName) {
      eventTimezoneName = position && position.event && position.event.timezoneName;
    }
    for (let field of positionDateTimeFields) {
      if (updatedPositionData[field]) {
        position[field] = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(updatedPositionData[field], eventTimezoneName);
      }
    }
  }

  /**
   * Create deep copy of a Claim
   * @param src
   * @param dst
   * @returns {*|Claim}
   */
  copy(src, dst) {
    if (!isNaN(parseFloat(src.amount))){
      src.amount = parseFloat(src.amount);
    }
    dst = cloneDeep(src);
    if (src.receiptFileForUpload) {
      dst.receiptFileForUpload = src.receiptFileForUpload;
    }
    return this.$q.when(dst)
      .then(claim => {
        return claim;
      })
  }

  savePrimaryClaimForStaffingUserPosition(staffingUser, position, eventTimezoneName = null) {
    const claim = position.primaryClaim;
    if (eventTimezoneName) {
      eventTimezoneName = (position && position.event && position.event.timezoneName)
        || (claim && claim.event && claim.event.timezoneName);
    }
    const data = this.serializePrimaryClaim(claim, position, eventTimezoneName);
    const url = savePrimaryClaimForStaffingUserPath
      .replace('{{staffing_users_id}}', staffingUser.id)
      .replace('{{positions_id}}', position.id);
    return this.APIService.post(url, data)
      .catch(response => {
        this.LogService.error("Error posting to url", url, "response:", response);
        return this.$q.reject(createError({
          error: 'ErrorSavingClaim',
          message: 'There was an error saving the claim',
          response,
        }));
      })
      .then(response => {
        this.updatePositionFromAPI(position, response.data.position, eventTimezoneName);
        return this.create(response.data.primary_claim);
      });
  }

  saveOtherUsersPrimaryClaimForStaffingUserPosition(staffingUser, position, claim) {
    const eventTimezoneName = (claim && claim.event && claim.event.timezoneName)
      || (position && position.event && position.event.timezoneName);
    const data = this.serializePrimaryClaim(claim, position);
    const url = savePrimaryClaimForOtherStaffingUserPath
      .replace('{{staffing_users_id}}', staffingUser.id)
      .replace('{{positions_id}}', position.id);
    return this.APIService.post(url, data)
      .catch(response => {
        this.LogService.error("Error posting to url", url, "response:", response);
        return this.$q.reject(createError({
          error: 'ErrorSavingClaim',
          message: 'There was an error saving the claim',
          response,
        }));
      })
      .then(response => {
        this.updatePositionFromAPI(position, response.data.position, eventTimezoneName);
        return this.create(response.data.primary_claim);
      });
  }

  saveSecondaryClaimForStaffingUser(claim, staffingUser) {
    const data = this.serialize(claim);
    const url = saveSecondaryClaimForStaffingUserPath
      .replace('{{staffing_users_id}}', staffingUser.id)
      .replace('{{events_id}}', claim.events_id);
    return this.APIService.post(url, data)
      .catch(response => {
        return this.$q.reject(createError({
          error: 'ErrorSavingClaim',
          message: 'There was an error saving the claim',
          response,
        }));
      })
      .then(response => {
        return this.create(response.data)
          .catch(err => {
            this.LogService.error("Error creating claim after saving:", err);
            return this.$q.reject(err);
          });
      })
      .then(updatedClaim => {
        // Upload document if present
        if (claim.receiptDocument && claim.receiptDocument.hasFileForUpload && !claim.receiptDocument.isUploaded) {
          const receiptPath = uploadSecondaryClaimReceipt
            .replace('{{staffing_users_id}}', staffingUser.id)
            .replace('{{events_id}}', updatedClaim.events_id)
            .replace('{{claims_id}}', updatedClaim.id);
          const receiptUrl = this.APIService.convertPathToURL(receiptPath);
          return this.DocumentService.upload(claim.receiptDocument, receiptUrl, null, 'receipt')
            .then(() => {
              return updatedClaim;
            });
        } else {
          return updatedClaim;
        }
      })
  }

  withdrawClaimForStaffingUser(claim, staffingUser) {
    const url = withdrawSecondaryClaim
      .replace('{{staffing_users_id}}', staffingUser.id)
      .replace('{{events_id}}', claim.events_id)
      .replace('{{claims_id}}', claim.id);
    return this.APIService.post(url)
      .catch(response => {
        this.LogService.error("Error withdrawing claim; response:", response);
        return this.$q.reject(createError({
          error: 'ErrorWithdrawingClaim',
          message: 'There was an error withdrawing the claim',
          response,
        }));
      })
      .then(response => {
        return this.ClaimStatusService.getWithdrawnStatus()
          .then(withdrawnStatus => {
            claim.status = withdrawnStatus;
            return claim;
          });
      });
  }

  getOrCreatePrimaryClaimForPosition(staffUser, position, data = null, config = null) {
    return this.getPrimaryClaimForPosition(staffUser, position, config)
      .then(claim => {
        if (claim) {
          return claim;
        }
        return this.createPrimaryClaimForPosition(staffUser, position, data);
      })
      .then(claim => {
        position.primaryClaim = claim;
        return claim;
      });
  }

  createPrimaryClaimForPosition(staffUser, position, data = null) {
    data = data || {};
    const newData = {
      staffing_users_id: staffUser.id,
      claim_start_time: position.start_time,
      claim_end_time: position.end_time,
      primary_claim: true,
      position,
      ...data
    };
    return this.createForUser(staffUser, newData)
      .then(claim => {
        return this.ClaimCategoryService.getPrimaryClaimCategory()
          .then(category => {
            claim.category = category;
            return claim;
          });
      })
  }

  createOtherUsersPrimaryClaimForPosition(position, data = null) {
    data = data || {};
    const newData = {
      staffing_users_id: position.staffing_users_id,
      claim_start_time: position.start_time,
      claim_end_time: position.end_time,
      position,
      ...data
    };
    return this.create(newData)
      .then(claim => {
        return this.ClaimCategoryService.getPrimaryClaimCategory()
          .then(category => {
            claim.category = category;
            return claim;
          })
      })
      .then(claim => {
        position.primaryClaim = claim;
        return claim;
      });
  }

  createSecondaryClaimForPosition(staffUser, position, data = null) {
    data = data || {};
    data.events_id = position && position.events_id;
    return this.createForUser(staffUser, data);
  }

  getIncrementalDataStoreForStaffingUser(staffingUser) {
    const staffingUsersId = normalizeIdentifier(staffingUser.id);
    const syncWindowDefinition = staffingUser.syncWindowDefinition;
    const dataStoreTypeKey = `Claim:user=${staffingUsersId}:window=${syncWindowDefinition.name}`;
    if (this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey] === undefined) {
      const factory = (data, ...args) => {
        return this.create(data, ...args);
      };
      const basePath = getArchiveClaimsForStaffingUserPath
        .replace('{{staffing_users_id}}', staffingUsersId);
      const path = this.APIService.addURLParamsFromObject(basePath, syncWindowDefinition.params);
      const dataStore = this.IncrementalDataStoreService.getOrBuild(dataStoreTypeKey, path, factory);
      this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey] = dataStore;

      if (INCREMENTAL_PURGE_OLD_DATA_STORE) {
        const destroyPromises = Object.keys(this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName)
          .filter(currKey => currKey !== dataStoreTypeKey)
          .map(keyToDelete => {
            delete this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[keyToDelete];
            return this.$q.when(this.IncrementalDataStoreService.destroy(keyToDelete));
          });
        if (destroyPromises.length) {
          this.$q.all(destroyPromises)
            .then(() => {
              this.LogService.debug(`ClaimService purged previous data stores`);
            });
        }
      }
    }

    this._mostRecentDataStore = this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey];
    return this.$q.when(this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey]);
  }

  getLocalById(id) {
    return this.$q.when(this._mostRecentDataStore)
      .then(dataStore => {
        if (!dataStore) {
          return null;
        }
        return dataStore.getLocalById(id);
      });
  }

  getAllForStaffUser(staffingUser, config = null) {
    return this.getIncrementalDataStoreForStaffingUser(staffingUser)
      .then(dataStore => dataStore.getAll(config))
      .catch(response => {
        this.LogService.error("Error retrieving positions (ClaimService.getAllForStaffingUser):)", response);
        return this.$q.reject(createError({
          error: 'ErrorRetrievingStaffingUserClaims',
          message: 'Error retrieving claims from server',
          response
        }));
      });
  }

  getAllStaffUsersClaimsForEvent(staffUser, event, config = null) {
    if (!event.id) {
      return this.$q.reject(createError({
        error: 'MissingEventForClaims',
        message: 'Missing event for claims',
      }));
    }
    let eventId = normalizeIdentifier(event.id);

    return this.getAllForStaffUser(staffUser, config)
      .then(claims => {
        return claims.filter(claim => normalizeIdentifier(claim.events_id) === eventId)
      });
  }

  getPrimaryClaimsForEvent(staffUser, event, config) {
    return this.getAllStaffUsersClaimsForEvent(staffUser, event, config)
      .then(claims => {
        return claims;
      })
      .then(claims => claims.filter(claim => claim.isPrimaryClaim))
      .then(claims => {
        return claims;
      })
  }

  getPrimaryClaimForPosition(staffUser, position, config = null) {
    return this.getPrimaryClaimsForEvent(staffUser, position.event, config)
      .then(claims => {
        return claims;
      })
      .then(eventClaims => {
        const positionClaims = eventClaims.filter(claim => parseInt(claim.events_staffing_days_positions_id) === parseInt(position.id));
        return positionClaims.shift();
      });
  }

  getSecondaryClaimsForEvent(staffUser, event, config = null) {
    return this.getAllStaffUsersClaimsForEvent(staffUser, event, config)
      .then(claims => claims.filter(claim => claim.isSecondaryClaim));
  }

  descriptiveTitleForClaim(claim) {
    if (!claim) {
      return '';
    }
    const titleParts = [];
    if (claim.category) {
      titleParts.push(this.LanguageService.translateProperty(claim.category, 'name'));
    }
    if (claim.type) {
      titleParts.push(this.LanguageService.translateProperty(claim.type, 'name'));
    }
    if (claim.category && claim.category.isAmountBased && claim.amount !== null) {
      titleParts.push(formatCurrency(claim.amount));
    }
    return titleParts.join(' - ');
  }

}

export default ClaimService;

m.service('ClaimService', ClaimService);
